export type TallyPaymentLedgers = {
  cashLedger: string;
  bankLedger: string;
};

export type TallyHallConfig = TallyPaymentLedgers & {
  incomeLedger: string;
  costCategory: string;
  costCentre: string;
};

export type TallySabilTypeConfig = {
  incomeLedger: string;
  costCentre: string;
};

export type TallySabilTypeKey = "CHULA" | "ESTABLISHMENT" | "MUTTAVATTEEN" | "MUTTAVATTEEN_YEARLY";

export type TallyLagatConfig = TallyPaymentLedgers & {
  incomeLedger: string;
  costCategory: string;
  costCentre: string;
};

/** Income + cost mapping for one FMB stream (Annual or Contribution). */
export type TallyFmbStreamConfig = {
  incomeLedger: string;
  costCategory: string;
  costCentre: string;
};

export type TallyFmbConfig = TallyPaymentLedgers & {
  annual: TallyFmbStreamConfig;
  contribution: TallyFmbStreamConfig;
};

export type TallySabilConfig = TallyPaymentLedgers & {
  costCategory: string;
  CHULA: TallySabilTypeConfig;
  ESTABLISHMENT: TallySabilTypeConfig;
  MUTTAVATTEEN: TallySabilTypeConfig;
  MUTTAVATTEEN_YEARLY: TallySabilTypeConfig;
};

export type TallyConfig = {
  lagat: TallyLagatConfig;
  sabil: TallySabilConfig;
  rent: {
    halls: Record<string, TallyHallConfig>;
  };
  niyaaz: TallyLagatConfig;
  yearlyNiyaaz: TallyLagatConfig;
  fmb: TallyFmbConfig;
};

export type TallyRentHallRow = TallyHallConfig & { hallName: string };

const emptyPaymentLedgers = (): TallyPaymentLedgers => ({
  cashLedger: "",
  bankLedger: "",
});

function parsePaymentLedgers(
  obj: Record<string, unknown>,
  legacy?: TallyPaymentLedgers
): TallyPaymentLedgers {
  const cash = pickString(obj, "cashLedger");
  const bank = pickString(obj, "bankLedger");
  return {
    cashLedger: cash || legacy?.cashLedger || "",
    bankLedger: bank || legacy?.bankLedger || "",
  };
}

function paymentLedgersReady(ledgers: TallyPaymentLedgers): boolean {
  return Boolean(ledgers.cashLedger.trim() && ledgers.bankLedger.trim());
}

const emptyLagatShapedConfig = (): TallyLagatConfig => ({
  ...emptyPaymentLedgers(),
  incomeLedger: "",
  costCategory: "",
  costCentre: "",
});

const emptyFmbStreamConfig = (): TallyFmbStreamConfig => ({
  incomeLedger: "",
  costCategory: "",
  costCentre: "",
});

const emptyFmbConfig = (): TallyFmbConfig => ({
  ...emptyPaymentLedgers(),
  annual: emptyFmbStreamConfig(),
  contribution: emptyFmbStreamConfig(),
});

export function defaultTallyConfig(): TallyConfig {
  return {
    lagat: emptyLagatShapedConfig(),
    sabil: {
      ...emptyPaymentLedgers(),
      costCategory: "",
      CHULA: { incomeLedger: "", costCentre: "" },
      ESTABLISHMENT: { incomeLedger: "", costCentre: "" },
      MUTTAVATTEEN: { incomeLedger: "", costCentre: "" },
      MUTTAVATTEEN_YEARLY: { incomeLedger: "", costCentre: "" },
    },
    rent: {
      halls: {},
    },
    niyaaz: emptyLagatShapedConfig(),
    yearlyNiyaaz: emptyLagatShapedConfig(),
    fmb: emptyFmbConfig(),
  };
}

export function emptyTallyHallConfig(): TallyHallConfig {
  return {
    ...emptyPaymentLedgers(),
    incomeLedger: "",
    costCategory: "",
    costCentre: "",
  };
}

function isHallConfigBlank(hall: TallyHallConfig): boolean {
  return !(
    hall.cashLedger?.trim() ||
    hall.bankLedger?.trim() ||
    hall.incomeLedger?.trim() ||
    hall.costCategory?.trim() ||
    hall.costCentre?.trim()
  );
}

function isHallConfigReady(hall: TallyHallConfig): boolean {
  return Boolean(
    paymentLedgersReady(hall) &&
    hall.incomeLedger.trim() &&
    hall.costCategory.trim() &&
    hall.costCentre.trim()
  );
}

/** Match saved rent hall config by exact or case-insensitive hall name. Blank rows are ignored. */
export function findSavedHallConfigEntry(
  halls: Record<string, TallyHallConfig>,
  hallName: string
): { key: string; config: TallyHallConfig } | null {
  const trimmed = hallName.trim();
  if (!trimmed) return null;
  if (halls[trimmed] && !isHallConfigBlank(halls[trimmed])) {
    return { key: trimmed, config: halls[trimmed] };
  }
  const lower = trimmed.toLowerCase();
  const match = Object.entries(halls).find(
    ([k, cfg]) => k.trim().toLowerCase() === lower && !isHallConfigBlank(cfg)
  );
  return match ? { key: match[0], config: match[1] } : null;
}

function findSavedHallConfig(
  halls: Record<string, TallyHallConfig>,
  hallName: string
): TallyHallConfig {
  return findSavedHallConfigEntry(halls, hallName)?.config ?? emptyTallyHallConfig();
}

/** One row per system hall (sorted). Saved config is applied only when the hall name matches. */
export function mergeRentHallRowsFromSystemHalls(
  config: TallyConfig,
  systemHallNames: string[]
): TallyRentHallRow[] {
  const saved = config.rent.halls;
  const systemNames = [...new Set(systemHallNames.map((n) => n.trim()).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })
  );

  return systemNames.map((hallName) => ({
    hallName,
    ...findSavedHallConfig(saved, hallName),
  }));
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function pickString(obj: Record<string, unknown>, key: string, fallback = ""): string {
  const v = obj[key];
  return typeof v === "string" ? v : fallback;
}

function parseSabilType(obj: unknown): TallySabilTypeConfig {
  const o = isRecord(obj) ? obj : {};
  return {
    incomeLedger: pickString(o, "incomeLedger"),
    costCentre: pickString(o, "costCentre"),
  };
}

function parseHall(obj: unknown, legacy?: TallyPaymentLedgers): TallyHallConfig {
  const o = isRecord(obj) ? obj : {};
  return {
    ...parsePaymentLedgers(o, legacy),
    incomeLedger: pickString(o, "incomeLedger"),
    costCategory: pickString(o, "costCategory"),
    costCentre: pickString(o, "costCentre"),
  };
}

function parseLagatShaped(
  obj: Record<string, unknown>,
  legacy?: TallyPaymentLedgers
): TallyLagatConfig {
  return {
    ...parsePaymentLedgers(obj, legacy),
    incomeLedger: pickString(obj, "incomeLedger"),
    costCategory: pickString(obj, "costCategory"),
    costCentre: pickString(obj, "costCentre"),
  };
}

function parseFmbStream(obj: unknown): TallyFmbStreamConfig {
  const o = isRecord(obj) ? obj : {};
  return {
    incomeLedger: pickString(o, "incomeLedger"),
    costCategory: pickString(o, "costCategory"),
    costCentre: pickString(o, "costCentre"),
  };
}

function parseFmbConfig(
  obj: Record<string, unknown>,
  legacy?: TallyPaymentLedgers
): TallyFmbConfig {
  return {
    ...parsePaymentLedgers(obj, legacy),
    annual: parseFmbStream(obj.annual),
    contribution: parseFmbStream(obj.contribution),
  };
}

/** Merge API JSON with defaults so forms always have a full shape. */
export function normalizeTallyConfig(raw: unknown): TallyConfig {
  const base = defaultTallyConfig();
  if (!isRecord(raw)) {
    return base;
  }

  const legacyShared: TallyPaymentLedgers = {
    cashLedger: pickString(raw, "cashLedger"),
    bankLedger: pickString(raw, "bankLedger"),
  };

  const lagatRaw = isRecord(raw.lagat) ? raw.lagat : {};
  const sabilRaw = isRecord(raw.sabil) ? raw.sabil : {};
  const rentRaw = isRecord(raw.rent) ? raw.rent : {};
  const hallsRaw = isRecord(rentRaw.halls) ? rentRaw.halls : {};
  const niyaazRaw = isRecord(raw.niyaaz) ? raw.niyaaz : {};
  const yearlyNiyaazRaw = isRecord(raw.yearlyNiyaaz) ? raw.yearlyNiyaaz : {};
  const fmbRaw = isRecord(raw.fmb) ? raw.fmb : {};

  const halls: Record<string, TallyHallConfig> = {};
  for (const [name, hall] of Object.entries(hallsRaw)) {
    halls[name] = parseHall(hall, legacyShared);
  }
  return {
    lagat: parseLagatShaped(lagatRaw, legacyShared),
    sabil: {
      ...parsePaymentLedgers(sabilRaw, legacyShared),
      costCategory: pickString(sabilRaw, "costCategory"),
      CHULA: parseSabilType(sabilRaw.CHULA ?? base.sabil.CHULA),
      ESTABLISHMENT: parseSabilType(sabilRaw.ESTABLISHMENT ?? base.sabil.ESTABLISHMENT),
      MUTTAVATTEEN: parseSabilType(sabilRaw.MUTTAVATTEEN ?? base.sabil.MUTTAVATTEEN),
      MUTTAVATTEEN_YEARLY: parseSabilType(
        sabilRaw.MUTTAVATTEEN_YEARLY ?? sabilRaw.ESTABLISHMENT ?? base.sabil.MUTTAVATTEEN_YEARLY
      ),
    },
    rent: { halls },
    niyaaz: parseLagatShaped(niyaazRaw, legacyShared),
    yearlyNiyaaz: parseLagatShaped(yearlyNiyaazRaw, legacyShared),
    fmb: parseFmbConfig(fmbRaw, legacyShared),
  };
}

export function tallyConfigToRentHallRows(config: TallyConfig): TallyRentHallRow[] {
  return Object.entries(config.rent.halls).map(([hallName, hall]) => ({
    hallName,
    ...hall,
  }));
}

/** Persist only halls with at least one ledger field set (blank system halls must not poison export readiness). */
export function rentHallRowsToRecord(rows: TallyRentHallRow[]): Record<string, TallyHallConfig> {
  const halls: Record<string, TallyHallConfig> = {};
  for (const row of rows) {
    const name = row.hallName?.trim();
    if (!name) continue;
    const hall: TallyHallConfig = {
      cashLedger: row.cashLedger?.trim() ?? "",
      bankLedger: row.bankLedger?.trim() ?? "",
      incomeLedger: row.incomeLedger?.trim() ?? "",
      costCategory: row.costCategory?.trim() ?? "",
      costCentre: row.costCentre?.trim() ?? "",
    };
    if (isHallConfigBlank(hall)) continue;
    halls[name] = hall;
  }
  return halls;
}

export function isLagatTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  if (!config) return false;
  return isLagatShapedConfigReady(config.lagat);
}

function isLagatShapedConfigReady(block: TallyLagatConfig): boolean {
  if (!paymentLedgersReady(block)) return false;
  return Boolean(block.incomeLedger.trim() && block.costCategory.trim() && block.costCentre.trim());
}

export function isNiyaazTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  if (!config) return false;
  return isLagatShapedConfigReady(config.niyaaz);
}

export function isYearlyNiyaazTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  if (!config) return false;
  return isLagatShapedConfigReady(config.yearlyNiyaaz);
}

function isSabilTypeReady(type: TallySabilTypeConfig): boolean {
  return Boolean(type.incomeLedger.trim() && type.costCentre.trim());
}

/** Ready when shared sabil ledgers are set and at least one sabil type is mapped (unused types may stay empty). */
export function isSabilTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  if (!config) return false;
  const { sabil } = config;
  if (!paymentLedgersReady(sabil)) return false;
  if (!sabil.costCategory.trim()) return false;
  return (["CHULA", "ESTABLISHMENT", "MUTTAVATTEEN", "MUTTAVATTEEN_YEARLY"] as const).some((key) =>
    isSabilTypeReady(sabil[key])
  );
}

function isFmbStreamReady(stream: TallyFmbStreamConfig): boolean {
  return Boolean(
    stream.incomeLedger.trim() && stream.costCategory.trim() && stream.costCentre.trim()
  );
}

/** Ready when cash/bank and Annual stream are fully mapped (leftover uses Annual). */
export function isFmbTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  if (!config) return false;
  if (!paymentLedgersReady(config.fmb)) return false;
  return isFmbStreamReady(config.fmb.annual);
}

/** Ready when at least one hall is fully mapped. Unmapped halls are skipped at export time. */
export function isRentTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  if (!config) return false;
  const halls = Object.values(config.rent.halls);
  return halls.some(isHallConfigReady);
}

/** @deprecated Use module-specific readiness checks in export buttons. */
export function isTallyConfigReady(config: TallyConfig | null | undefined): boolean {
  return isLagatTallyConfigReady(config);
}
